import { useCallback, useEffect, useRef, useSyncExternalStore, type MouseEvent } from 'react'
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom'

// R460: the order page's Back returns to the screen HOP itself came from, and to the Board
// otherwise. "Previous screen" means a screen INSIDE HOP (Frank z#64594): an order opened from
// WhatsApp, a notification or a typed link has a browser history entry before it that is not
// HOP's, and history.back() would take the person out of the app. So the record is in memory
// only: an entry counts as reached in-app when HOP's own router pushed it during this page
// load. A reload or a cold open starts with an empty record, so Back goes to the Board.
// Navigation only: nothing here is stored, fetched or sent.
//
// R492 (Dee via Rook #66417): this record is the app's OWN navigation stack, and every screen's
// Back (the one in CommandHeader) reads it. Each entry HOP pushed points at the entry it was
// pushed from, so the chain from the current entry is the stack; Back pops one link. The
// browser's back is mapped to the same Back: on a deep link (no HOP entry beneath it) one Board
// entry is written beneath the page before the router starts (seedBoardBeneathDeepLink), so the
// browser's back lands on the Board, exactly where Back goes, instead of leaving the app.

// location.key of an entry HOP pushed -> location.key of the entry it was pushed from.
const previousOf = new Map<string, string>()
let lastKey: string | null = null

// Subscribers re-render when the record changes (the tracker writes it in an effect, after the
// screen's first render, so a reader must hear about it).
const listeners = new Set<() => void>()
let version = 0
function changed(): void {
  version += 1
  listeners.forEach((l) => l())
}
function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function _resetInAppHistoryForTest(): void {
  previousOf.clear()
  lastKey = null
  changed()
}

export function hasInAppPrevious(key: string): boolean {
  return previousOf.has(key)
}

/** True when the entry `key` has an in-app previous screen; re-renders when the record moves. */
export function useHasInAppPrevious(key: string): boolean {
  useSyncExternalStore(subscribe, () => version)
  return previousOf.has(key)
}

// Marks on the two history entries the seed writes. The router keeps both: it spreads the
// existing state when it stamps its own index on an entry.
const SEED_BOARD_KEY = 'hop-seed-board'
const SEED_TOP_KEY = 'hop-seed-top'

// A deep link to one of these needs no Board beneath it: it is the Board, a home screen, or not
// a screen Back applies to.
const NO_SEED = new Set(['/', '/board', '/cockpit', '/login', '/change-password', '/kitchen', '/kitchen/tv'])

/**
 * R492: called once, before the router reads window.history (App.tsx). A cold open of a
 * sub-screen gets one Board entry written beneath it, so the browser's back goes to the Board
 * like the in-app Back does. A reload of a page already seeded is left alone (its state keeps
 * the mark), so reloads never stack Board entries.
 */
export function seedBoardBeneathDeepLink(basename: string): void {
  if (typeof window === 'undefined') return
  const state = window.history.state as { hopSeeded?: unknown } | null
  if (state && state.hopSeeded === true) return
  const base = basename === '/' ? '' : basename
  const { pathname, search, hash } = window.location
  if (base && !pathname.startsWith(base)) return
  const inApp = pathname.slice(base.length) || '/'
  if (NO_SEED.has(inApp.replace(/\/$/, '') || '/')) return
  window.history.replaceState({ key: SEED_BOARD_KEY }, '', `${base}/board`)
  window.history.pushState({ key: SEED_TOP_KEY, hopSeeded: true }, '', `${pathname}${search}${hash}`)
}

// Rendered once inside the router (App.tsx); renders nothing.
export function InAppHistoryTracker() {
  const location = useLocation()
  const type = useNavigationType()
  useEffect(() => {
    if (lastKey !== null && lastKey !== location.key) {
      if (type === 'PUSH') previousOf.set(location.key, lastKey)
      // A replace (NewOrderScreen's create -> /orders/:id) takes over the replaced entry's
      // place, so it keeps that entry's previous screen.
      else if (type === 'REPLACE' && previousOf.has(lastKey)) previousOf.set(location.key, previousOf.get(lastKey)!)
    }
    lastKey = location.key
    changed()
  }, [location.key, type])
  return null
}

// The click handler for a Link that opens an order (Rook z#64675, Castle z#64056/#64670): one
// activation makes one history entry. A double tap used to push two /orders/:id entries before
// the first navigation rendered, so the first Back stayed on the order. A second activation
// while the first is in flight is ignored: the first push stays the only entry and the tracker
// above records it, so Back steps back to the Board with its tab. As a backstop, an activation
// whose target the browser is already on replaces that entry instead of pushing a twin. A
// modified or middle click (new tab, new window) is left to the browser and the Link.
export function useOpenOnce(to: string): (e: MouseEvent<HTMLAnchorElement>) => void {
  const navigate = useNavigate()
  const location = useLocation()
  const opening = useRef(false)
  useEffect(() => {
    opening.current = false
  }, [location.key])
  return useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) return
      if (opening.current) return e.preventDefault()
      if (e.currentTarget.pathname === window.location.pathname) {
        e.preventDefault()
        navigate(to, { replace: true })
        return
      }
      opening.current = true
    },
    [navigate, to],
  )
}

// The Back handler for a link whose href is the Board. One tap moves one screen: a second
// tap before the first navigation lands is ignored (a double tap used to skip two screens).
// R492: with no in-app previous screen the Board REPLACES the page (it used to be pushed). A
// pushed Board would itself have an in-app previous (the page just left), so its Back would
// lead back to that page and the two would cycle, growing history on every tap. Replacing
// keeps the Board a root with no Back, and still never steps the browser's history.
export function useInAppBack(fallback = '/board'): (e: MouseEvent) => void {
  const navigate = useNavigate()
  const location = useLocation()
  const leaving = useRef(false)
  useEffect(() => {
    leaving.current = false
  }, [location.key])
  return useCallback(
    (e: MouseEvent) => {
      e.preventDefault()
      if (leaving.current) return
      leaving.current = true
      if (hasInAppPrevious(location.key)) navigate(-1)
      else navigate(fallback, { replace: true })
    },
    [navigate, location.key, fallback],
  )
}
